/**
 * Static preview of the slider control cluster for the editor canvas.
 *
 * The editor renders slides as a plain flex row with no Embla instance, so the
 * controls registry never runs there and a placement/nav/dots selection would
 * otherwise be invisible. This mirrors the class structure SliderControlsMarkup
 * emits — enough for the inspector choice to mean something on screen — while
 * staying deliberately inert:
 *
 *   - no Embla instance (a second init path would fight the editor iframe)
 *   - no fill/progress animation (a looping animation in the canvas is noise)
 *   - buttons are not focusable and carry no handlers
 *
 * Structure only, no state: any drift from the PHP output stays cosmetic.
 */
import { Fragment } from "@wordpress/element";

const PREV_PATH = "M6 11L1 6L6 1";
const NEXT_PATH = "M1 1L6 6L1 11";

function Arrow({ direction }) {
  return (
    <button
      type="button"
      className={`embla__button embla__button--${direction}`}
      tabIndex={-1}
      aria-hidden="true"
    >
      <svg
        className="embla__button__svg"
        width="7"
        height="12"
        viewBox="0 0 7 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d={direction === "prev" ? PREV_PATH : NEXT_PATH}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

/** Zero-pad to two digits, mirroring the fraction renderer in the registry. */
function pad(n) {
  return (n < 10 ? "0" : "") + n;
}

function InertBullet({ selected }) {
  return (
    <button
      type="button"
      className={selected ? "embla__dot embla__dot--selected" : "embla__dot"}
      tabIndex={-1}
      aria-hidden="true"
    />
  );
}

/**
 * The pagination, in whichever shape the chosen dots style renders.
 *
 * A dots style is a frontend renderer keyed off `data-dots-style` — the registry
 * hands it the `.embla__dots` node and it fills it in. None of that runs in the
 * canvas, and no stylesheet keys off `data-dots-style` either, so each style
 * needs its shape reproduced here or every selection previews as the default
 * bullets. That is why this is shared rather than inlined: a canvas preview that
 * builds its own `.embla__dots` silently ignores the author's choice.
 *
 * `progress-pill` and `progressbar` are registered by the Brandy THEME, not by
 * this plugin, and their skins live in the theme's stylesheet. Naming them here
 * is a deliberate exception to the "plugin does not know theme keys" rule the
 * options dropdowns follow: it was chosen over adding a preview filter, and the
 * cost is that the next theme-registered variant needs a plugin change to
 * preview. Neither reproduces the FILL — that is driven by an Embla instance the
 * canvas does not have — only the shape and the current position.
 *
 * @param {Object}   props
 * @param {number}   props.count        Number of snaps to paginate.
 * @param {string}   props.style        Resolved dots variant key.
 * @param {number}   props.position     Zero-based active snap.
 * @param {Function} props.renderBullet Optional bullet renderer, `(index,
 *                                      isSelected) => Element`. Omitted for the
 *                                      inert skeleton; supplied by previews whose
 *                                      strip actually scrolls, so their dots can
 *                                      click through to a slide.
 * @return {Element} The pagination node.
 */
export function SliderDots({
  count,
  style = "default",
  position = 0,
  renderBullet,
}) {
  const total = Math.max(1, count);
  const active = Math.min(Math.max(position, 0), total - 1);

  // `fraction` replaces the bullets outright with `01 / 06` text and a modifier
  // class.
  if (style === "fraction") {
    return (
      <div className="embla__dots embla__dots--fraction">
        {`${pad(active + 1)} / ${pad(total)}`}
      </div>
    );
  }

  // One track for the whole slider, no bullets at all. The theme's renderer owns
  // the container outright and writes this same single span into it.
  //
  // The fill is `scaleX(var(--embla-progressbar-progress, 0))`, and nothing in
  // the canvas drives that variable — left alone the bar previews as an empty
  // 2px line, which tells the author nothing about what they just picked. The
  // snaps travelled so far are shown instead, expressed as a fraction of the
  // track: still at rest on an inert skeleton, and moving with the strip on a
  // preview that scrolls.
  if (style === "progressbar") {
    return (
      <div
        className="embla__dots embla__dots--progressbar"
        style={{ '--embla-progressbar-progress': (active + 1) / total }}
      >
        <span className="embla__progressbar__fill" role="presentation" />
      </div>
    );
  }

  // Ordinary bullets under a modifier class — the theme's renderer delegates to
  // the built-in dots for the markup and only adds the class, so the preview
  // does the same. Left without `is-filling`, which is the resting state the
  // theme's CSS already styles for a slider that is not currently advancing.
  const className =
    style === "progress-pill"
      ? "embla__dots embla__dots--progress-pill"
      : "embla__dots";

  return (
    <div className={className}>
      {/* Fragment, not a key on the caller's element: it keys the list here so
          a renderBullet that forgets one cannot warn, and it adds no DOM, so
          `.embla__dots > .embla__dot` still matches. */}
      {Array.from({ length: total }, (_, index) => (
        <Fragment key={index}>
          {renderBullet ? (
            renderBullet(index, index === active)
          ) : (
            <InertBullet selected={index === active} />
          )}
        </Fragment>
      ))}
    </div>
  );
}

/**
 * Host class for a nav style that skins itself from an ancestor.
 *
 * `blank` (the theme's naked chevrons) is not different markup — its renderer
 * only puts a class on the slider host and delegates the buttons to the default
 * nav. The host in the canvas is the block's own wrapper, which this component
 * cannot reach, so the Edit components ask for the class and add it to their
 * `useBlockProps` themselves. Kept here so both derive it the same way.
 *
 * @param {string} navStyle Resolved nav variant key.
 * @return {string} Class to add to the preview host, or ''.
 */
export function navHostClass(navStyle) {
  return navStyle === "blank" ? "embla--nav-blank" : "";
}

/**
 * Approximate the real snap count without measuring: one snap per page of
 * slides, capped so a long slider does not render a wall of dots in the canvas.
 */
export function previewDotCount(slideCount, slidesPerView) {
  // parseFloat, not parseInt: a fractional per-view (1.15 to reveal the next
  // slide's edge) would otherwise floor to 1 and overstate the page count.
  const perView = Math.max(1, parseFloat(slidesPerView) || 1);

  return Math.min(8, Math.max(1, Math.ceil((slideCount || 1) / perView)));
}

export default function SliderControlsSkeleton({
  placement = "default",
  dotsStyle = "default",
  showArrows = true,
  showDots = true,
  dotCount = 3,
}) {
  if (!showArrows && !showDots) {
    return null;
  }

  const isGrouped = placement.startsWith("grouped-");
  const arrows = showArrows ? (
    <>
      <Arrow direction="prev" />
      <Arrow direction="next" />
    </>
  ) : null;

  if (isGrouped) {
    return (
      <div className="embla__controls">
        {showArrows && <Arrow direction="prev" />}
        {showDots && <SliderDots count={dotCount} style={dotsStyle} />}
        {showArrows && <Arrow direction="next" />}
      </div>
    );
  }

  return (
    <>
      {arrows && <div className="embla__buttons">{arrows}</div>}
      {showDots && <SliderDots count={dotCount} style={dotsStyle} />}
    </>
  );
}
